import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { secureGet } from "../config/api";
import { useTranslation } from "react-i18next";
import DynamicForm from "./DynamicForm";
import SnackbarNotification from './SnackbarNotification';

function ModuleWrapper({ moduleType, mode = "add", onClose, onSuccess, initialValues: propInitialValues }) {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const abortController = useMemo(() => new AbortController(), []);
  const signal = abortController.signal;
  const getStoredValues = (key) => {
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData) : {};
  };
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const [initialValues,] = useState(() => {
    if (moduleType === "settings") {
      return getStoredValues(moduleType);
    } else if (moduleType === "profile") {
      return getStoredValues("user");
    }
    return location.state?.initialValues || propInitialValues || {};
  });

  const getFormTitle = () => {
    return t(`form_titles.${mode === "modify" ? `edit_${moduleType}` : `add_${moduleType}`}`);
  };

  const [, setLoading] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({
    clients: [],
    repos: [],
    paymentMode: [],
    parentId: [],
    providers: [],
    products: [],
    families: [],
    brands: [],
    roles: [],
    cities: [],
    banks: [],
    expense_category: [],
    commercial: [],
    inventaires: [],
    responsableZone: [],
    zones: [],
  });


  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const options = {};

        if (moduleType === "users") {
          const { data: reposData } = await secureGet("/repos?size=1000", showSnackbar, { signal });
          options.repos = reposData.content.map((repo) => ({
            value: repo.id,
            label: repo.name,
            active: repo.active,
          }));

          const roleKeys = Object.keys(t("roles", { returnObjects: true }) || {});
          options.roles = roleKeys.map((roleKey) => ({
            value: roleKey,
            label: t(`roles.${roleKey}`),
          }));
        }

        if (moduleType === "inventories") {
          const { data: reposData } = await secureGet("/repos?size=1000", showSnackbar, { signal });
          options.repos = reposData.content.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));
        }

        if (moduleType === "inventory-zones") {
          const { data: inventairesData } = await secureGet("/inventories?size=1000", showSnackbar, { signal });
          options.inventaires = inventairesData.content.map((inventaire) => ({
            value: inventaire.id,
            label: inventaire.name,
          }));

          const { data: responsableZoneData } = await secureGet(`/users?role=ROLE_ZONE_MANAGER&size=1000`, showSnackbar, { signal });
          options.responsableZone = responsableZoneData.content.map((responsable) => ({
            value: responsable.id,
            label: `${responsable.firstName} ${responsable.lastName}`,
          }));
        }

        if (moduleType === "inventory-locations") {
          const { data: zonesData } = await secureGet("/inventory-zones?size=1000", showSnackbar, { signal });
          options.zones = zonesData.content.map((zone) => ({
            value: zone.id,
            label: zone.name,
          }));
        }

        setDropdownOptions((prevOptions) => ({ ...prevOptions, ...options }));
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };
    fetchOptions();

    return () => {
      abortController.abort();
    };
  }, [moduleType, signal, t, abortController]);



  const getFieldSpecs = () => {
    switch (moduleType) {
      case "users":
        return [
          { label: t(`fields.users.lastName`), name: "lastName", required: true },
          { label: t(`fields.users.firstName`), name: "firstName", required: true },
          { label: t(`fields.users.username`), name: "username", required: true },
          { label: t(`fields.users.email`), name: "email", required: true },
          {
            label: t(`fields.users.role`), name: "role", type: "dropdown", required: true,
            options: dropdownOptions.roles,
            conditionalFields: {
              ROLE_ZONE_MANAGER: [
                { name: "repoName", label: t('fields.users.repositories'), type: 'dropdown', options: dropdownOptions.repos },
              ],
              ROLE_INVENTORY_AGENT: [
                { name: "repoName", label: t('fields.users.repositories'), type: 'dropdown', options: dropdownOptions.repos },
              ],
            },
          },
          { label: t(`fields.users.password`), name: "password", type: "password", required: true },
          { label: t(`fields.users.passwordConfirmation`), name: "passwordConfirmation", type: "password", required: true, isPasswordConfirmation: true },
        ];
      case "repos":
        return [
          { label: t(`fields.repos.name`), name: "name", required: true },
        ];
      case "inventories":
        return [
          { label: t(`fields.inventory.name`), name: "name", required: true },
          { label: t(`fields.inventory.repo`), name: "repoId", type: "dropdown", required: true, options: dropdownOptions.repos },
        ];
      case "inventory-zones":
        return [
          { label: t(`fields.inventory.name`), name: "name", required: true },
          { label: t(`fields.inventory.inventaire`), name: "inventoryId", type: "dropdown", required: true, options: dropdownOptions.inventaires },
          { label: t(`fields.inventory.level`), name: "nbrLevel", type: "number", required: true },
          { label: t(`fields.inventory.bay`), name: "nbrBay", type: "number", required: true },
          { label: t(`fields.inventory.meanTimeBay`), name: "meanTimeBay", type: "number", required: false },
          { label: t(`fields.inventory.responsableZone`), name: "zoneManagerId", type: "dropdown", options: dropdownOptions.responsableZone },
        ];
      case "inventory-locations":
        return [
          { label: t(`fields.inventory.level`), name: "level", type: "number", required: true },
          { label: t(`fields.inventory.bay`), name: "bay", type: "number", required: true },
          { label: t(`fields.inventory.zone`), name: "zoneId", type: "dropdown", required: true, options: dropdownOptions.zones },
        ];
      default:
        return [];
    }
  };

  const getRedirectUrl = () => {
    switch (moduleType) {
      case "users":
        return "/utilisateurs";
      case "inventories":
        return "/inventaires";
      case "inventory-zones":
        return "/zones";
      case "inventory-locations":
        return "/emplacements";
      default:
        return "/";
    }
  };

  const redirectUrl = getRedirectUrl();

  const fields = getFieldSpecs().map((field) => ({
    ...field,
    value: mode === "modify" ? initialValues[field.name] : "",
  }));

  if (mode === "modify" && initialValues.id && moduleType !== "afinancier") {
    fields.push({ name: "id", value: initialValues.id, type: "hidden" });
  }

  const apiUrl = moduleType === "users" ? `/${moduleType}/add` : `/${moduleType}`;


  const handleFormSubmit = async (responseData) => {
    if (onClose) {
      onClose("submit");
    } else {
      history.push(redirectUrl);
    }
  };

  const handleSuccess = () => {
    const message = mode === 'modify'
      ? t(`confirmation.${moduleType}Edit`)
      : t(`confirmation.${moduleType}Add`);
    if (onSuccess) onSuccess(message);
    history.push({
      pathname: redirectUrl,
      state: {
        showSnackbar: true,
        snackbarMessage: message,
        snackbarSeverity: 'success'
      }
    });

    if (onClose) {
      onClose("submit");
    } else {
      history.push(redirectUrl);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose("cancel");
    } else {
      history.push(redirectUrl);
    }
  };

  const renderPopup = () => {
    const formTitle = getFormTitle();
    if (moduleType === "users" || moduleType === "repos" || moduleType === "inventories" || moduleType === "inventory-zones" || moduleType === "inventory-locations") {
      return (
        <DynamicForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={fields}
          products={dropdownOptions.products}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          apiUrl={apiUrl}
          module={moduleType}
        />
      )
    }
  };

  return (
    <div>
      {renderPopup()}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
}

export default ModuleWrapper;
